import { Tag } from '@innovationnorway/component-library';
import { Heading, Label, ValidationMessage } from '@innovationnorway/cl-react';
import React, { useMemo } from 'react';
import { CompanySizeCriteria, DeliveryType, OneWayInServiceUpsertDto } from 'src/api/v2';
import '../styles/SummaryPage.scss';
import DataTableV3 from 'src/components/DataTableV3';
import { eeaArticleColumns } from '../constants/eea-article-columns';
import useGeonorge from 'src/features/one-way-in/hooks/use-geonorge';
import useDeliveryTypeOptions from '../hooks/use-delivery-type-options';
import useTargetPageOptions from '../hooks/use-target-page-options';
import { sanitizeD365RichTextEditor } from 'src/features/one-way-in/utils/htmlTextConverter';
import { useTranslation } from 'react-i18next';
import useEeaArticles from '../hooks/use-eea-articles';
import useSummaryForm from '../hooks/subforms/use-summary-form';
import { SubFormProps } from '../hooks/use-service-sub-form';

const SummaryPage = (props: SubFormProps<OneWayInServiceUpsertDto>) => {
  const { data: upsertDto, errors, setErrors: addErrors } = props;
  const { countiesWithMunicipalities } = useGeonorge();
  const { companyAgeOptions, companySizeOptions } = useTargetPageOptions();
  const { deliveryTypeOptions } = useDeliveryTypeOptions(upsertDto.deliveryTypes);
  const { t: tOneWayIn } = useTranslation('oneWayIn');
  const { t: tServices } = useTranslation('services');
  const { t: tCommon } = useTranslation('common');
  const { eeaArticles } = useEeaArticles();

  const { about, target, contactInfo, delivery, eea } = useSummaryForm(upsertDto, errors, addErrors);

  const selectedDeliveryTypes = upsertDto.deliveryTypes;
  const selectedDeliveryTypeOptions = deliveryTypeOptions.filter((option) =>
    selectedDeliveryTypes.includes(option.value as DeliveryType),
  );

  const selectedEeaArticles = upsertDto.selectedEeaArticles;
  const selectedEeaArticleOptions = eeaArticles?.filter((article) =>
    selectedEeaArticles?.some((selected) => selected.eeaArticleId === article.id),
  );
  const selectedCounties = useMemo(() => {
    const selectedCountyNames = upsertDto.counties || [];
    const selectedMunicipalityIds = upsertDto.municipalities || [];

    const countiesWithIndividualMunicipalities = countiesWithMunicipalities.filter((county) => {
      const hasSelectedMunicipalities = county.kommuner.some((municipality) =>
        selectedMunicipalityIds.includes(municipality.kommunenummer),
      );
      return hasSelectedMunicipalities && !selectedCountyNames.includes(county.fylkesnavn);
    });

    const allRelevantCounties = [
      ...countiesWithMunicipalities.filter((county) => selectedCountyNames.includes(county.fylkesnavn)),
      ...countiesWithIndividualMunicipalities,
    ];

    return allRelevantCounties.map((county) => ({
      ...county,
      kommuner: county.kommuner.filter((municipality) =>
        selectedMunicipalityIds.includes(municipality.kommunenummer),
      ),
    }));
  }, [countiesWithMunicipalities, upsertDto.counties, upsertDto.municipalities]);

  const booleanToYesNo = (value: boolean | null | undefined) => {
    if (value?.toString() === 'true') {
      return 'Ja';
    } else if (value?.toString() === 'false') {
      return 'Nei';
    }
    return null;
  };

  return (
    <div>
      <Heading>{tServices('summary.title')}</Heading>
      <div className="display--flex flex-direction--column gap--5 margin-top--2">
        <div className="summary__card">
          <Heading className="margin-bottom--2" level={3}>
            {tServices('about.title')}
          </Heading>
          <div>
            <Field
              label={tServices('about.serviceName.label')}
              value={upsertDto.name || ''}
              error={errors.name?.message || ''}
              {...about.registerWithRules('name')}
            />

            <div className="display--flex gap--10">
              <Field
                label={tServices('about.validFrom.label')}
                value={upsertDto.validFrom ? new Date(upsertDto.validFrom).toLocaleDateString() : ''}
                error={errors.validFrom?.message || ''}
                {...about.registerWithRules('validFrom')}
              />
              <Field
                label={tServices('about.validTo.label')}
                value={upsertDto.validTo ? new Date(upsertDto.validTo).toLocaleDateString() : ''}
                error={errors.validTo?.message || ''}
                {...about.registerWithRules('validTo')}
              />
            </div>
            <Field
              label={tServices('about.purpose.label')}
              value={
                <div
                  dangerouslySetInnerHTML={{
                    __html: sanitizeD365RichTextEditor(upsertDto.servicePurpose || ''),
                  }}
                ></div>
              }
              error={errors.servicePurpose?.message || ''}
              {...about.registerWithRules('servicePurpose')}
            />
            <Field
              label={tServices('about.description.label')}
              value={
                <div
                  dangerouslySetInnerHTML={{
                    __html: sanitizeD365RichTextEditor(upsertDto.description || ''),
                  }}
                ></div>
              }
              error={errors.description?.message || ''}
              {...about.registerWithRules('description')}
            />
            <Field
              label={tServices('about.customerDescription.label')}
              value={
                <div
                  dangerouslySetInnerHTML={{
                    __html: sanitizeD365RichTextEditor(upsertDto.customerDescription || ''),
                  }}
                ></div>
              }
              error={errors.customerDescription?.message || ''}
              {...about.registerWithRules('customerDescription')}
            />
            <Field
              label={tServices('about.keywords.label')}
              value={
                upsertDto.keywords?.length !== 0
                  ? upsertDto.keywords?.map((kw) => {
                      return (
                        <Tag className="margin-right--1" key={kw} type="info">
                          {kw}
                        </Tag>
                      );
                    })
                  : []
              }
              error={errors.keywords?.message || ''}
              {...about.registerWithRules('keywords')}
            />
            <Field
              label={tServices('about.links.title')}
              value={upsertDto.externalLinks?.map((url) => {
                if (!url.url || !url.text) return null;
                return (
                  <p key={url.url}>
                    <a href={new URL(url.url).toString()} target="_blank" rel="noopener noreferrer">
                      {url.text}
                    </a>
                  </p>
                );
              })}
              error={errors.externalLinks?.message || ''}
            />
          </div>
        </div>

        <div className="summary__card">
          <Heading className="margin-bottom--2" level={3}>
            {tServices('target.title')}
          </Heading>
          <Field
            label={tServices('target.companyAge.label')}
            value={companyAgeOptions.find((option) => option.value === upsertDto.companyAge)?.text || ''}
            error={errors.companyAge?.message || ''}
            {...target.registerWithRules('companyAge')}
          />
          <Field
            label={tServices('target.companySize.label')}
            value={
              companySizeOptions
                .filter((option) => upsertDto.companySize?.includes(option.value as CompanySizeCriteria))
                .map((option) => option.text)
                .join(', ') || ''
            }
            error={errors.companySize?.message || ''}
            {...target.registerWithRules('companySize')}
          />
          <Field
            label={tServices('target.innovationLevelCriteria.label')}
            value={
              upsertDto.innovationLevel
                ? tServices(`target.innovationLevelCriteria.${upsertDto.innovationLevel}`)
                : ''
            }
            error={errors.innovationLevel?.message || ''}
            {...target.registerWithRules('innovationLevel')}
          />
          <Field
            label={tServices('target.districtPolicy.label')}
            value={booleanToYesNo(upsertDto.districtPolicyArea)}
            error={errors.districtPolicyArea?.message || ''}
            {...target.registerWithRules('districtPolicyArea')}
          />
          <Field
            label={tServices('target.geographic.label')}
            value={
              <ul>
                {selectedCounties.map((county) => (
                  <li key={county.fylkesnavn}>
                    <strong>{county.fylkesnavn}</strong>
                    {county.kommuner.length > 0 && (
                      <div>
                        {county.kommuner.map((municipality) => municipality.kommunenavnNorsk).join(', ')}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            }
            error={errors.counties?.message || ''}
            {...target.registerWithRules('counties')}
          />
        </div>

        <div className="summary__card">
          <Heading className="margin-bottom--2" level={3}>
            {tServices('delivery.title')}
          </Heading>
          <Field
            label={tServices('delivery.type.label')}
            value={
              selectedDeliveryTypeOptions.length !== 0
                ? selectedDeliveryTypeOptions?.map((dt) => {
                    return (
                      <Tag className="margin-right--1" key={dt.value} type="info">
                        {String(dt.text)}
                      </Tag>
                    );
                  })
                : []
            }
            error={errors.deliveryTypes?.message || ''}
            {...delivery.registerWithRules('deliveryTypes')}
          />
          <Field
            label={tServices('delivery.description.label')}
            value={
              <div
                dangerouslySetInnerHTML={{
                  __html: sanitizeD365RichTextEditor(upsertDto.deliveryDescription || ''),
                }}
              ></div>
            }
            error={errors.deliveryDescription?.message || ''}
            {...delivery.registerWithRules('deliveryDescription')}
          />
          <Field
            label={tServices('delivery.deadline.question')}
            value={
              upsertDto.applicationDeadline
                ? new Date(upsertDto.applicationDeadline).toLocaleDateString()
                : tCommon('no')
            }
            error={errors.applicationDeadline?.message || ''}
            {...delivery.registerWithRules('applicationDeadline')}
          />
          <Field
            label={tServices('delivery.userFee.label')}
            {...delivery.registerWithRules('hasUserFee')}
            value={booleanToYesNo(upsertDto.hasUserFee)}
            error={errors.hasUserFee?.message || ''}
          />
          {upsertDto.hasUserFee?.toString() === 'true' && (
            <Field
              label={tServices('delivery.userFee.comment.label')}
              value={
                <div
                  dangerouslySetInnerHTML={{
                    __html: sanitizeD365RichTextEditor(upsertDto.userFeeComments || ''),
                  }}
                ></div>
              }
              {...delivery.registerWithRules('userFeeComments')}
              error={errors.userFeeComments?.message || ''}
            />
          )}
        </div>

        <div className="summary__card">
          <Heading className="margin-bottom--2" level={3}>
            {tServices('contact.title')}
          </Heading>
          <Field
            label={tServices('contact.name.label')}
            value={upsertDto.contactName || ''}
            error={errors.contactName?.message || ''}
            {...contactInfo.registerWithRules('contactName')}
          />
          <Field
            label={tServices('contact.email.label')}
            value={upsertDto.contactEmail || ''}
            error={errors.contactEmail?.message || ''}
            {...contactInfo.registerWithRules('contactEmail')}
          />
          <Field
            label={tServices('contact.phone.label')}
            value={upsertDto.contactPhone || ''}
            error={errors.contactPhone?.message || ''}
            {...contactInfo.registerWithRules('contactPhone')}
          />
          <Field
            label={tServices('contact.supplier.question')}
            value={upsertDto.businessName || ''}
            error={errors.businessName?.message || ''}
          />
          <Field
            label={tServices('contact.client.question')}
            value={
              <span>{upsertDto.sponsors?.map((sponsor) => tOneWayIn(`sponsors.${sponsor}`)).join(', ')}</span>
            }
            error={errors.sponsors?.message || ''}
            {...contactInfo.registerWithRules('sponsors')}
          />
        </div>

        <div className="summary__card">
          <Heading className="margin-bottom--2" level={3}>
            {tServices('eea.title')}
          </Heading>
          {selectedEeaArticles.length > 0 && (
            <Field
              label={tServices('eea.articles.question')}
              value={
                <DataTableV3
                  disableSelection
                  disableColumnEditor
                  disableGlobalFilter
                  disableExport
                  hideFilterText
                  data={selectedEeaArticleOptions || []}
                  columns={eeaArticleColumns}
                  id={'eeaArticles'}
                />
              }
            />
          )}
          <Field
            label={
              upsertDto.selectedEeaArticles.length > 0
                ? tServices('eea.comments.label')
                : tServices('eea.comments.outsideLabel')
            }
            value={
              <div
                dangerouslySetInnerHTML={{
                  __html: sanitizeD365RichTextEditor(upsertDto.eeaComments || ''),
                }}
              ></div>
            }
            error={errors.eeaComments?.message || ''}
            {...eea.registerWithRules('eeaComments')}
          />
        </div>
      </div>
    </div>
  );
};

export default SummaryPage;

const Field = ({
  label,
  value,
  error,
}: {
  label: string;
  value: string | React.ReactNode;
  error?: string;
}) => {
  return (
    <div>
      <div className="display--flex flex-direction--column margin-bottom--2">
        <Label className="margin-bottom--1">{label}</Label>
        <span>{value}</span>
        {error && <ValidationMessage>{error}</ValidationMessage>}
      </div>
    </div>
  );
};
